import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const JAVACourse = () => {
  const testimonials = [
    {
      id: 1,
      name: "Saurabh Kumar",
      review:
        "The Java course at Nadezhda Venture was incredibly comprehensive. I started with basic knowledge and now feel confident in Spring Boot and microservices. The project-based learning was a game-changer!",
      rating: 5,
      date: "2024-07-28",
      profileImg: "https://randomuser.me/api/portraits/men/35.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Pooja Singh",
      review:
        "Learning Core Java, then moving to advanced topics like JDBC and Spring was smooth. The instructors are very supportive and clarified every doubt. Best Java training in Noida for sure!",
      rating: 4,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/women/48.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Amit Sharma",
      review:
        "I landed a great backend developer role after completing this course. The focus on real-time projects and placement assistance made all the difference. Highly recommend for Java aspirants.",
      rating: 5,
      date: "2024-07-22",
      profileImg: "https://randomuser.me/api/portraits/men/53.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Divya Gupta",
      review:
        "The course covers everything from basic Java to enterprise-level frameworks. The practical sessions on Spring MVC and Hibernate were particularly helpful. A very valuable learning experience.",
      rating: 5,
      date: "2024-07-20",
      profileImg: "https://randomuser.me/api/portraits/women/63.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Ravi Shankar",
      review:
        "Excellent curriculum and patient instructors. I appreciate the emphasis on clean code and best practices. The full stack project at the end prepared me perfectly for the industry.",
      rating: 4,
      date: "2024-07-18",
      profileImg: "https://randomuser.me/api/portraits/men/78.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "What is Java and why should I learn it?",
      answer:
        "Java is a highly popular, object-oriented, class-based, concurrent, secured and general-purpose computer programming language. It's used for developing enterprise-level applications, Android apps, big data applications, web applications, and more. Learning Java opens up vast career opportunities due to its widespread industry adoption and versatility.",
    },
    {
      title: "What are the prerequisites for joining this Java course?",
      answer:
        "This course is designed for both beginners and those with some programming background. No prior Java experience is strictly required, but a basic understanding of programming concepts (like variables, loops, functions) is beneficial. We start from Core Java fundamentals.",
    },
    {
      title: "What topics are covered in your Java training program?",
      answer:
        "Our comprehensive Java course covers Core Java (OOPs, data types, control statements, exceptions, collections, multithreading), JDBC, Servlets & JSP, Maven/Gradle, Spring Framework (Core, MVC, Security, REST), Hibernate, Spring Boot, and an introduction to microservices concepts. You'll also work on live projects.",
    },
    {
      title: "How is the training delivered (online/offline)?",
      answer:
        "We offer flexible training modes: intensive offline classroom sessions at our Noida center and interactive live online classes. Both modes feature hands-on coding, expert instructors, real-time projects, and a supportive learning environment.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Yes, we provide dedicated placement assistance to our Java graduates. This includes resume building, mock interviews, soft skills training, and connecting you with hiring partners for roles like Java Developer, Backend Developer, and Full Stack Developer.",
    },
    {
      title: "What kind of projects will I work on during the training?",
      answer:
        "You will work on multiple practical projects, including a major Capstone Project. These projects will involve building real-world applications such as an e-commerce platform, a banking application, or a RESTful API using Spring Boot, helping you build a strong portfolio.",
    },
    {
      title: "Is there a certification provided after the Java course?",
      answer:
        "Yes, upon successful completion of the course and project work, you will receive a professional Java Development certification from Appwars Technologies, which is recognized by the industry.",
    },
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const curriculumContent = [
    {
      title: "Module 1: Core Java Fundamentals",
      description: "Introduction to Java, JDK, JRE, JVM, Data Types, Operators, Control Statements, Loops, Arrays, Strings, and basic I/O operations.",
    },
    {
      title: "Module 2: Object-Oriented Programming (OOPs) in Java",
      description: "Concepts of Class, Object, Encapsulation, Inheritance, Polymorphism, Abstraction, Interfaces, and Packages.",
    },
    {
      title: "Module 3: Exception Handling & Multithreading",
      description: "Understanding exceptions, try-catch-finally, checked vs. unchecked exceptions, threads, runnable interface, thread synchronization, and deadlock.",
    },
    {
      title: "Module 4: Collections Framework",
      description: "Deep dive into ArrayList, LinkedList, HashSet, HashMap, TreeSet, TreeMap, and their practical usage for data structures.",
    },
    {
      title: "Module 5: JDBC (Java Database Connectivity)",
      description: "Connecting Java applications to databases, performing CRUD operations, executing SQL queries, and managing transactions.",
    },
    {
      title: "Module 6: Servlets and JSP (Java Server Pages)",
      description: "Building dynamic web applications with Java Servlets and JSP, understanding HTTP methods, session management, and MVC architecture in web context.",
    },
    {
      title: "Module 7: Maven/Gradle Build Automation",
      description: "Introduction to project build tools, dependency management, and project lifecycle with Maven/Gradle.",
    },
    {
      title: "Module 8: Spring Framework Core",
      description: "In-depth understanding of Inversion of Control (IoC) and Dependency Injection (DI) with Spring Container, Beans, and Annotations.",
    },
    {
      title: "Module 9: Spring MVC (Model-View-Controller)",
      description: "Developing web applications using Spring MVC architecture, Controllers, View Resolvers, and data binding.",
    },
    {
      title: "Module 10: Hibernate Framework",
      description: "Object-Relational Mapping (ORM) with Hibernate, mapping entities, HQL, Criteria API, and integrating Hibernate with Spring.",
    },
    {
      title: "Module 11: Spring Boot & RESTful Web Services",
      description: "Building microservices and RESTful APIs rapidly with Spring Boot, Spring Data JPA, and Postman for API testing.",
    },
    {
      title: "Module 12: Project Work & Best Practices",
      description: "Develop a complete, real-world full-stack Java application integrating all learned frameworks. Focus on clean code, design patterns, and deployment strategies.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Java Course in Noida | Core Java, Spring Boot, Microservices</title>
        <meta
          name="description"
          content="Enroll in the best Java training in Noida. Master Core Java, Advanced Java, Spring Framework, Hibernate, Spring Boot, and build enterprise-level applications with 100% placement support."
        />
        <meta
          name="keywords"
          content="Java course Noida, Core Java training, Advanced Java, Spring Framework, Spring Boot, Hibernate, Java developer course, Full Stack Java, Java certification, Backend development, Microservices Java"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://media.istockphoto.com/id/871030872/photo/programming-code-abstract-technology-background-of-software-developer.jpg?s=612x612&w=0&k=20&c=lkfUNy8Sf3TpFBeIfpBAy6FB5XQwbwjdzypK_1uOQd8="
          alt="Java Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight"
            >
              Master <span className="text-orange-400">Java Development</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200"
            >
              Become a proficient Java developer, from core concepts to advanced frameworks like Spring Boot and Microservices.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
               <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("JAVA Development Course")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn Java Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Learn Java?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            Java remains one of the most powerful and widely used programming languages globally, powering enterprise applications, Android development, and big data systems. Its "write once, run anywhere" philosophy ensures its enduring relevance.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-orange-700 mb-3">Enterprise Powerhouse</h3>
              <p className="text-gray-600">Dominates backend development for large-scale systems.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-orange-700 mb-3">Android Ecosystem</h3>
              <p className="text-gray-600">Fundamental language for native Android application development.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-orange-700 mb-3">High Demand & Stability</h3>
              <p className="text-gray-600">Consistent job market with stable career growth.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🚀', title: 'Comprehensive Full Stack Coverage', desc: 'From Core Java to advanced Spring Boot and microservices.' },
              { icon: '💻', title: 'Hands-on Project-Based Learning', desc: 'Build multiple real-world applications for a strong portfolio.' },
              { icon: '👨‍🏫', title: 'Experienced Industry Instructors', desc: 'Learn best practices from veteran Java developers.' },
              { icon: '🎓', title: 'Industry-Recognized Certification', desc: 'Receive a professional certificate to boost your career prospects.' },
              { icon: '🤝', title: 'Dedicated Placement Assistance', desc: 'Extensive support for interviews, resume building, and job placements.' },
              { icon: '⚙️', title: 'Master Development Tools', desc: 'Gain proficiency in Eclipse, Maven, Git, and various databases.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-green-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-green-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Technologies You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our Java course ensures you gain proficiency in all essential development tools and frameworks for enterprise application development.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'Java (JDK 8/11+)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
              { name: 'Spring Boot', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg' },
              { name: 'Hibernate', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/hibernate/hibernate-original.svg' },
              { name: 'Maven', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/maven/maven-original.svg' },
              { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
              { name: 'Eclipse IDE', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/eclipse/eclipse-original.svg' },
              { name: 'IntelliJ IDEA', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/intellij/intellij-original.svg' },
              { name: 'MySQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
              { name: 'PostgreSQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
              { name: 'Postman', logo: 'https://w7.pngwing.com/pngs/28/245/png-transparent-postman-hd-logo-thumbnail.png' }, // Generic API icon, Postman is for testing it
              { name: 'Junit', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/junit/junit-plain.svg' },
              
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-green-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A Java development certification from us will open doors to a wide range of high-demand roles in the software industry.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '👨‍💻', title: 'Java Developer', desc: 'Design, develop, and maintain Java-based applications and systems.' },
              { icon: '🖥️', title: 'Backend Developer', desc: 'Build server-side logic, databases, and APIs for web applications.' },
              { icon: '📱', title: 'Android Developer', desc: 'Create robust and scalable mobile applications for the Android platform.' },
              { icon: '🚀', title: 'Full Stack Developer', desc: 'Work on both front-end (e.g., React/Angular) and backend (Java) development.' },
              { icon: '☁️', title: 'Cloud Developer', desc: 'Develop and deploy Java applications on cloud platforms like AWS, Azure, GCP.' },
              { icon: '💼', title: 'Software Engineer', desc: 'Contribute to the full software development lifecycle of complex systems.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-green-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our Java Development course.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Java Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-orange-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-900">
            What Our Java Development Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-orange-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-orange-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-orange-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

      {/* Conditionally render the popup */}
            {isPopupOpen && (
              <JoinNowFormPopup
                onClose={handleClosePopup}
                courseTitle={selectedCourseTitle}
              />
            )}
    </div>
  );
};

export default JAVACourse;